import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Creating an AI Slack Bot\npageTitle: Creating Slack bots - Mendable\ndescription: Creating Slack bots with Mendable.\n---\n\n\nMendable provides an easy way to create a Slack bot that can answer questions about your project. This guide will show you how to create a Slack bot that uses the Mendable API to answer questions and provide sources for the answers. It creates a new thread for each question asked and stores the history of the conversation.\n\n\n\n## Prerequisites\n\n- Node.js\n- A Slack workspace\n- Mendable AI API key\n\n## Installation\n\n1. Clone the repository to your local machine.\n2. Run `npm install` to install all the necessary dependencies.\n3. Create a `.env` file in the root directory of the project and add the following environment variables:\n\n```\nMENDABLE_API_KEY=<Your Mendable API Key>\nSLACK_BOT_TOKEN=<Your Slack Bot Token>\nSLACK_SIGNING_SECRET=<Your Slack Signing Secret>\nSLACK_APP_TOKEN=<Your Slack App Token>\n```\n\n## Slack App Setup\n\n1. Create a new app in your Slack workspace.\n2. Navigate to the \"Socket Mode\" section under \"Settings\" and enable it.\n3. Navigate to the \"Basic Information\" section under \"Settings\" and scroll down to \"App Credentials\". Here, you will find your \"Signing Secret\" and \"App Token\" (under \"Tokens for Your Workspace\").\n4. Navigate to the \"OAuth & Permissions\" section under \"Features\". Here, you will find your \"Bot Token\".\n5. In the \"Scopes\" section, add the following bot token scopes: `app_mentions:read`, `channels:history`, `channels:read`, `chat:write`, `commands`, `groups:history`, `groups:read`, `im:history`, `im:read`, `im:write`, `mpim:history`, `mpim:read`, `mpim:write`.\n6. Enable Event Subscriptions under \"Features\" and add the following event subscriptions: `app_mention`.\n7. Install the app to your workspace.\n\n## Usage\n\n1. Run `node index.js` or `npm run start` to start the bot.\n2. Add the bot to a channel in your Slack workspace.\n3. Mention the bot in a message with a question. For example: `@MendableAI How to create a product copilot?`\n4. The bot will respond with an answer in a thread and provide sources for its information.\n\n## Features\n\n- The bot maintains a history of conversations, allowing it to provide context-aware answers.\n- The bot provides sources for its answers, ensuring transparency and verifiability of information.\n- The bot responds to direct messages as well as mentions in channels.\n\n## Contributing\n\nPull requests are welcome. For major changes, please open an issue first to discuss what you would like to change.\n";
const filepath = "/integrations/slack.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
